import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Mail, Search } from 'lucide-react';
import type { AssetDetail, CustodyNotification, Meta } from '@skhub/contracts';
import { useToast } from '@/app/toast';
import { ApiError } from '@/lib/api';
import { useAssetAction, useEmployees, useMeta } from '@/lib/queries';
import { Avatar, Field, FormError, Modal } from '@/components/ui';
import { useDebounced } from './AssetsPage';

export type ActionKind = 'assign' | 'return' | 'status' | 'retire' | 'reinstate';

/** One dialog host for every lifecycle action on an asset. */
export function AssetActionDialog({ kind, asset, meta, onClose }: { kind: ActionKind | null; asset: AssetDetail; meta: Meta; onClose: () => void }) {
  if (!kind) return null;
  const props = { asset, meta, onClose };
  const titles: Record<ActionKind, string> = {
    assign: asset.status === meta.assignedStatus ? 'Reassign asset' : 'Assign asset',
    return: 'Return asset',
    status: 'Change status',
    retire: 'Retire asset',
    reinstate: 'Reinstate asset',
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={titles[kind]} description={<span className="mono">{asset.assetTag} · {asset.name}</span>}>
      {kind === 'assign' && <AssignForm {...props} />}
      {kind === 'return' && <ReturnForm {...props} />}
      {kind === 'status' && <StatusForm {...props} />}
      {kind === 'retire' && <RetireForm {...props} />}
      {kind === 'reinstate' && <ReinstateForm {...props} />}
    </Modal>
  );
}

type FormProps = { asset: AssetDetail; meta: Meta; onClose: () => void };

/** What the API reports about the confirmation email after an assign or return. */
export function emailNote(n: CustodyNotification | undefined) {
  if (!n) return '';
  if (n.status === 'sent') return ` · Email sent to ${n.to}${n.cc ? `, cc ${n.cc}` : ''}`;
  if (n.status === 'saved') return ` · Email saved to the outbox for ${n.to}${n.cc ? ` (cc ${n.cc})` : ''}`;
  if (n.status === 'failed') return ` · Email not sent: ${n.detail}`;
  return n.detail.startsWith('Email turned off') ? '' : ` · ${n.detail}`;
}

function useSubmit<T>(asset: AssetDetail, action: ActionKind, onClose: () => void, success: string) {
  const m = useAssetAction<T>(asset.id, action);
  const toast = useToast();
  const [error, setError] = useState<unknown>(null);
  // The version the user saw when the dialog opened. A background refresh must not swap in a newer one, or
  // e.g. "Assign" would silently reassign an asset someone else assigned meanwhile instead of reporting it.
  const openedVersion = useRef(asset.rowVersion);
  const submit = async (body: T) => {
    setError(null);
    try {
      const res = (await m.mutateAsync({ ...body, rowVersion: openedVersion.current })) as AssetDetail & { notification?: CustodyNotification };
      const failed = res.notification?.status === 'failed';
      toast({ kind: failed ? 'error' : 'success', title: success, detail: `${asset.assetTag}${emailNote(res.notification)}` });
      onClose();
    } catch (e) {
      setError(e);
    }
  };
  return { submit, busy: m.isPending, error, fields: (error instanceof ApiError && error.fields) || {} };
}

function Footer({ busy, label, danger, onClose }: { busy: boolean; label: string; danger?: boolean; onClose: () => void }) {
  return (
    <div className="mt-5 flex justify-end gap-2 border-t border-line pt-4">
      <button type="button" className="btn btn-ghost" onClick={onClose}>
        Cancel
      </button>
      <button type="submit" disabled={busy} className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}>
        {busy ? 'Saving…' : label}
      </button>
    </div>
  );
}

function Form({ onSubmit, children }: { onSubmit: () => void; children: ReactNode }) {
  return (
    <form
      noValidate
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-4"
    >
      {children}
    </form>
  );
}

function AssignForm({ asset, meta, onClose }: FormProps) {
  const reassign = asset.status === meta.assignedStatus;
  const s = useSubmit(asset, 'assign', onClose, reassign ? 'Asset reassigned' : 'Asset assigned');
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 200);
  const people = useEmployees({ q: dq || undefined, pageSize: 8, sort: 'displayName' });
  const [employeeId, setEmployeeId] = useState('');
  const [picked, setPicked] = useState<{ name: string; code: string; email: string } | null>(null);
  const [notify, setNotify] = useState(true);
  const [handover, setHandover] = useState('');
  const [ticket, setTicket] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [local, setLocal] = useState('');

  return (
    <Form
      onSubmit={() => {
        if (!employeeId) return setLocal('Choose who receives this asset');
        setLocal('');
        void s.submit({ rowVersion: asset.rowVersion, employeeId, handoverCondition: handover || null, ticketReference: ticket, reason, note, notify });
      }}
    >
      <FormError error={s.error} fieldsInline />
      {reassign && asset.assignee && (
        <p className="rounded-[var(--radius-md)] bg-surface-2 px-3 py-2 text-[13px] text-fg-2">
          Currently with <span className="font-medium text-fg">{asset.assignee.displayName}</span>. Their assignment closes when you confirm.
        </p>
      )}
      <fieldset>
        <legend className="label">
          Employee <span className="text-danger">*</span>
        </legend>
        <div className="relative">
          <Search aria-hidden className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-3" />
          <input aria-label="Search employees" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or employee ID" className="field pl-9" autoFocus />
        </div>
        <div role="radiogroup" aria-label="Matching employees" className="mt-2 max-h-56 overflow-y-auto rounded-[var(--radius-md)] border border-line">
          {people.isError ? (
            <p className="px-3 py-3 text-xs text-danger">Could not load employees.</p>
          ) : !people.data ? (
            <p className="px-3 py-3 text-xs text-fg-3">Loading…</p>
          ) : people.data.items.filter((e) => e.isActive).length === 0 ? (
            <p className="px-3 py-3 text-xs text-fg-3">No active employee matches.</p>
          ) : (
            people.data.items
              .filter((e) => e.isActive)
              .map((e) => {
                const isCurrent = asset.assignee?.id.toLowerCase() === e.id.toLowerCase();
                const checked = employeeId === e.id;
                return (
                  <label key={e.id} className={`flex items-center gap-3 border-b border-line px-3 py-2 last:border-0 ${isCurrent ? 'opacity-50' : 'hover:bg-surface-2'} ${checked ? 'bg-brand-soft' : ''}`}>
                    <input
                      type="radio"
                      name="employee"
                      value={e.id}
                      disabled={isCurrent}
                      checked={checked}
                      onChange={() => {
                        setEmployeeId(e.id);
                        setPicked({ name: e.displayName, code: e.employeeCode, email: e.email });
                      }}
                      className="accent-[var(--brand)]"
                    />
                    <Avatar name={e.displayName} size={26} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] text-fg">{e.displayName}</span>
                      <span className="block truncate text-xs text-fg-3">
                        <span className="mono">{e.employeeCode}</span>
                        {e.team ? ` · ${e.team}` : ''}
                        {e.laptopCount ? ` · holds ${e.laptopCount} laptop${e.laptopCount > 1 ? 's' : ''}` : ''}
                      </span>
                    </span>
                    {isCurrent && <span className="text-2xs text-fg-3">current</span>}
                  </label>
                );
              })
          )}
        </div>
        {(local || s.fields.employeeId) && <p className="mt-1 text-xs text-danger">{local || s.fields.employeeId}</p>}
        {picked && <p className="mt-1 text-xs text-fg-2">Selected: {picked.name} ({picked.code})</p>}
      </fieldset>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Handover condition" htmlFor="a-cond" error={s.fields.handoverCondition}>
          <select id="a-cond" className="field" value={handover} onChange={(e) => setHandover(e.target.value)}>
            <option value="">Not recorded</option>
            {meta.handoverConditions.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Ticket reference" htmlFor="a-ticket" error={s.fields.ticketReference}>
          <input id="a-ticket" className="field mono" value={ticket} onChange={(e) => setTicket(e.target.value)} maxLength={160} />
        </Field>
      </div>
      <Field label="Reason" htmlFor="a-reason" error={s.fields.reason}>
        <input id="a-reason" className="field" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={160} placeholder="e.g. New joiner, replacement" />
      </Field>
      <Field label="Note" htmlFor="a-note" error={s.fields.note}>
        <textarea id="a-note" className="field" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </Field>
      <NotifyToggle checked={notify} onChange={setNotify} who={picked?.name} email={picked?.email} kind="assigned" />
      <Footer busy={s.busy} label={reassign ? 'Reassign' : 'Assign'} onClose={onClose} />
    </Form>
  );
}

function ReturnForm({ asset, meta, onClose }: FormProps) {
  const s = useSubmit(asset, 'return', onClose, 'Asset returned');
  const targets = meta.statuses.filter((x) => x !== meta.assignedStatus && x !== meta.retiredStatus);
  const [target, setTarget] = useState(targets[0] ?? '');
  const [cond, setCond] = useState('');
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  return (
    <Form onSubmit={() => void s.submit({ rowVersion: asset.rowVersion, targetStatus: target, returnCondition: cond || null, note, notify })}>
      <FormError error={s.error} fieldsInline />
      {asset.assignee && (
        <p className="text-[13px] text-fg-2">
          Returning from <span className="font-medium text-fg">{asset.assignee.displayName}</span>.
        </p>
      )}
      <fieldset>
        <legend className="label">Goes to</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {targets.map((t) => (
            <label key={t} className={`flex cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border px-3 py-2.5 text-[13px] ${target === t ? 'border-brand bg-brand-soft text-fg' : 'border-line text-fg-2 hover:bg-surface-2'}`}>
              <input type="radio" name="target" value={t} checked={target === t} onChange={() => setTarget(t)} className="accent-[var(--brand)]" />
              {t}
            </label>
          ))}
        </div>
      </fieldset>
      <Field label="Condition on return" htmlFor="r-cond" error={s.fields.returnCondition}>
        <select id="r-cond" className="field" value={cond} onChange={(e) => setCond(e.target.value)}>
          <option value="">Not recorded</option>
          {meta.handoverConditions.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </Field>
      <Field label="Note" htmlFor="r-note" error={s.fields.note}>
        <textarea id="r-note" className="field" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
      </Field>
      <NotifyToggle checked={notify} onChange={setNotify} who={asset.assignee?.displayName} kind="returned" />
      <Footer busy={s.busy} label="Confirm return" onClose={onClose} />
    </Form>
  );
}

function StatusForm({ asset, meta, onClose }: FormProps) {
  const s = useSubmit(asset, 'status', onClose, 'Status changed');
  const options = meta.statuses.filter((x) => x !== meta.assignedStatus && x !== meta.retiredStatus && x !== asset.status);
  const [status, setStatus] = useState(options[0] ?? '');
  const [note, setNote] = useState('');
  return (
    <Form onSubmit={() => void s.submit({ rowVersion: asset.rowVersion, status, note })}>
      <FormError error={s.error} fieldsInline />
      <Field label="New status" htmlFor="s-status" error={s.fields.status}>
        <select id="s-status" className="field" value={status} onChange={(e) => setStatus(e.target.value)}>
          {options.map((o) => (
            <option key={o}>{o}</option>
          ))}
        </select>
      </Field>
      <Field label="Reason" htmlFor="s-note" error={s.fields.note} required hint="Recorded in the audit trail.">
        <textarea id="s-note" className="field" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} aria-invalid={!!s.fields.note} />
      </Field>
      <Footer busy={s.busy} label="Change status" onClose={onClose} />
    </Form>
  );
}

function RetireForm({ asset, meta, onClose }: FormProps) {
  const s = useSubmit(asset, 'retire', onClose, 'Asset retired');
  const [method, setMethod] = useState('');
  const [reason, setReason] = useState('');
  const [san, setSan] = useState('');
  return (
    <Form onSubmit={() => void s.submit({ rowVersion: asset.rowVersion, method, reason, sanitization: san || null })}>
      <FormError error={s.error} fieldsInline />
      <p className="rounded-[var(--radius-md)] bg-warn-soft px-3 py-2 text-xs text-warn">Retiring removes the asset from service. It can be reinstated later, and the retirement stays in its history.</p>
      <Field label="Method" htmlFor="t-method" error={s.fields.method} required>
        <select id="t-method" className="field" value={method} onChange={(e) => setMethod(e.target.value)} aria-invalid={!!s.fields.method}>
          <option value="">Choose a method</option>
          {meta.retirementMethods.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
      </Field>
      <Field label="Data sanitisation" htmlFor="t-san" error={s.fields.sanitization} hint="Has storage been wiped before the device leaves?">
        <select id="t-san" className="field" value={san} onChange={(e) => setSan(e.target.value)}>
          <option value="">Not recorded</option>
          {meta.sanitizationStates.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
      </Field>
      <Field label="Reason" htmlFor="t-reason" error={s.fields.reason} required>
        <textarea id="t-reason" className="field" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-invalid={!!s.fields.reason} />
      </Field>
      <Footer busy={s.busy} label="Retire asset" danger onClose={onClose} />
    </Form>
  );
}

function ReinstateForm({ asset, onClose }: FormProps) {
  const s = useSubmit(asset, 'reinstate', onClose, 'Asset reinstated');
  const [reason, setReason] = useState('');
  return (
    <Form onSubmit={() => void s.submit({ rowVersion: asset.rowVersion, reason })}>
      <FormError error={s.error} fieldsInline />
      <Field label="Reason" htmlFor="i-reason" error={s.fields.reason} required>
        <textarea id="i-reason" className="field" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-invalid={!!s.fields.reason} autoFocus />
      </Field>
      <Footer busy={s.busy} label="Reinstate" onClose={onClose} />
    </Form>
  );
}

/** "Email the employee" switch for assign and return, with who will receive it. */
export function NotifyToggle({ checked, onChange, who, email, kind }: { checked: boolean; onChange: (v: boolean) => void; who?: string; email?: string; kind: 'assigned' | 'returned' }) {
  const from = useMeta().data?.mailFrom ?? 'the IT asset mailbox';
  return (
    <label className={`flex items-start gap-3 rounded-[var(--radius-md)] border px-3 py-2.5 transition-colors ${checked ? 'border-brand/40 bg-brand-soft/60' : 'border-line'}`}>
      <input type="checkbox" className="mt-0.5 accent-[var(--brand)]" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="min-w-0 text-[13px]">
        <span className="flex items-center gap-1.5 font-medium text-fg">
          <Mail aria-hidden className="size-3.5 text-brand-ink" /> Email {kind === 'assigned' ? 'an assignment' : 'a return'} confirmation
        </span>
        <span className="mt-0.5 block text-xs text-fg-3">
          From {from} to {who ?? 'the employee'}
          {email ? ` (${email})` : ''}, with their manager in CC when one is recorded. Includes the full device details.
        </span>
      </span>
    </label>
  );
}
